import { useEffect } from "react";
import { Capacitor } from "@capacitor/core";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

/**
 * Registers the device for push notifications and stores the FCM/APNs token
 * in `push_tokens` for the currently authenticated user.
 *
 * No-op on web — only runs on native iOS/Android Capacitor builds.
 */
export const usePushNotifications = () => {
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;
    if (!Capacitor.isNativePlatform()) return;

    let cleanupFns: Array<() => void> = [];

    const setup = async () => {
      try {
        const { PushNotifications } = await import(
          "@capacitor/push-notifications"
        );

        // 1. Permission
        let perm = await PushNotifications.checkPermissions();
        if (perm.receive === "prompt") {
          perm = await PushNotifications.requestPermissions();
        }
        if (perm.receive !== "granted") {
          console.log("[push] permission not granted");
          return;
        }

        // 2. Register with APNs / FCM
        await PushNotifications.register();

        // 3. Persist the token
        const regListener = await PushNotifications.addListener(
          "registration",
          async (token) => {
            const platform = Capacitor.getPlatform() as
              | "ios"
              | "android"
              | "web";
            const { error } = await supabase.from("push_tokens").upsert(
              {
                user_id: user.id,
                token: token.value,
                platform,
                is_active: true,
              },
              { onConflict: "user_id,token" }
            );
            if (error) {
              console.error("[push] failed to save token", error);
            } else {
              console.log("[push] token saved");
            }
          }
        );

        const errListener = await PushNotifications.addListener(
          "registrationError",
          (err) => {
            console.error("[push] registration error", err);
          }
        );

        cleanupFns = [
          () => regListener.remove(),
          () => errListener.remove(),
        ];
      } catch (e) {
        console.error("[push] setup failed", e);
      }
    };

    setup();

    return () => {
      cleanupFns.forEach((fn) => fn());
    };
  }, [user]);
};
